@import "tailwindcss";

/* Archivo, self-hosted, plus the Bluebird redesign's scoped rules. Both come
   AFTER Tailwind so the `.bluebird` scope can restore content-box over
   preflight's global border-box — the comp's geometry depends on it. */
@import "./archivo.css";
@import "./bluebird.css";

@custom-variant dark (&:is(.dark *));

/*
  .container — site-wide page gutter. Tailwind v4 ships a default
  `container` utility that only sets `width: 100%` and stepped
  responsive `max-width` values (no centering, no padding), which
  caused marketing-page sections to render edge-to-edge while the
  Header's hand-rolled wrapper stayed centered. Override via
  `@utility` so every bare `<div className="container">` matches
  the header gutter.

  Max width is 1536px (matches max-w-screen-2xl). 1280px (max-w-7xl)
  felt cramped on ultrawide displays — header CTAs sat 700+ px from
  the viewport right edge on a 2880px monitor, which read as
  "misaligned" even though it was technically centered.
*/
@utility container {
  width: 100%;
  max-width: 96rem;
  margin-inline: auto;
  padding-inline: calc(var(--spacing) * 4);
  @media (width >= 40rem) {
    padding-inline: calc(var(--spacing) * 6);
  }
  @media (width >= 64rem) {
    padding-inline: calc(var(--spacing) * 8);
  }
}

:root {
  /* Font CSS variables — Next.js used `next/font` to inject these; in Rails we
     declare them here so the @theme aliases resolve. Fallback stacks are
     intentionally rich so the page stays legible if Google Fonts is blocked
     or slow (Lighthouse, ad-blockers, China, offline). */
  --font-poppins: 'Poppins', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  --font-dm-sans: 'DM Sans', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  --font-caveat: 'Caveat', 'Brush Script MT', cursive;
  /* Source globals.css references --font-geist-mono but Next.js never injects
     it (Caveat is imported, not Geist Mono). Pin a monospace fallback so the
     `font-mono` utility doesn't resolve to an empty value. */
  --font-geist-mono: ui-monospace, 'Cascadia Code', 'Source Code Pro', Menlo, Consolas, monospace;
}

@theme inline {
  --color-background: var(--background);
  --color-foreground: var(--foreground);
  --font-sans: var(--font-dm-sans);
  --font-headline: var(--font-poppins);
  --font-handwritten: var(--font-caveat);
  --font-mono: var(--font-geist-mono);

  /* Colorado Flag Official Colors */
  --color-colorado-blue: #002868;
  --color-colorado-gold: #996B00;
  --color-colorado-gold-light: #D4A017;
  --color-colorado-red: #BF0A30;
  --color-mountain-charcoal: #2C3E50;
  --color-snow-white: #FAFAFA;

  /* Component colors mapped to theme */
  --color-sidebar-ring: var(--sidebar-ring);
  --color-sidebar-border: var(--sidebar-border);
  --color-sidebar-accent-foreground: var(--sidebar-accent-foreground);
  --color-sidebar-accent: var(--sidebar-accent);
  --color-sidebar-primary-foreground: var(--sidebar-primary-foreground);
  --color-sidebar-primary: var(--sidebar-primary);
  --color-sidebar-foreground: var(--sidebar-foreground);
  --color-sidebar: var(--sidebar);
  --color-chart-5: var(--chart-5);
  --color-chart-4: var(--chart-4);
  --color-chart-3: var(--chart-3);
  --color-chart-2: var(--chart-2);
  --color-chart-1: var(--chart-1);
  --color-ring: var(--ring);
  --color-input: var(--input);
  --color-border: var(--border);
  --color-destructive: var(--destructive);
  --color-accent-foreground: var(--accent-foreground);
  --color-accent: var(--accent);
  --color-muted-foreground: var(--muted-foreground);
  --color-muted: var(--muted);
  --color-secondary-foreground: var(--secondary-foreground);
  --color-secondary: var(--secondary);
  --color-primary-foreground: var(--primary-foreground);
  --color-primary: var(--primary);
  --color-popover-foreground: var(--popover-foreground);
  --color-popover: var(--popover);
  --color-card-foreground: var(--card-foreground);
  --color-card: var(--card);
  --radius-sm: calc(var(--radius) - 4px);
  --radius-md: calc(var(--radius) - 2px);
  --radius-lg: 1.75rem;
  --radius-xl: calc(var(--radius) + 4px);
  --radius-2xl: calc(var(--radius) + 8px);
  --radius-3xl: calc(var(--radius) + 12px);
  --radius-4xl: calc(var(--radius) + 16px);
}

:root {
  /* Warm-tinted shadow tokens — port of design-r2 styles.css values.
     These power Tailwind v4 arbitrary utilities like shadow-[var(--shadow-md)]
     used by Home page atoms / sections (15-2). */
  --shadow-sm: 0 1px 2px rgba(180, 140, 80, 0.18);
  --shadow-md: 0 6px 18px rgba(180, 140, 80, 0.20);
  --shadow-lg: 0 18px 40px rgba(180, 140, 80, 0.25);
  --shadow-glow: 0 0 0 1px rgba(0,40,104,0.10), 0 22px 44px rgba(180,140,80,0.28);
  /* Resting card shadows — used by BikeCard (md) and FilterSidebar (sm).
     Tuned warm to match the cream palette; complements --shadow-glow on hover. */
  --shadow-sm: 0 1px 2px rgba(180, 140, 80, 0.10), 0 1px 3px rgba(180, 140, 80, 0.08);
  --shadow-md: 0 4px 12px rgba(180, 140, 80, 0.14), 0 2px 4px rgba(180, 140, 80, 0.08);
  --border-strong: #D6C49E;
  --font-hand: var(--font-handwritten);
  --font-head: var(--font-poppins);
  --font-body: var(--font-dm-sans);
  --radius: 1.25rem; /* RETRO: Bigger, friendlier corners */
  /* Colorado-themed light mode - RETRO WARM */
  --background: #FFF8E7; /* Warm golden cream instead of stark white */
  --background-warm: #FFF3D6; /* Even warmer for accent sections */
  --foreground: #2C3E50;
  --card: #FFFDF8; /* Warm off-white cards */
  --card-foreground: #2C3E50;
  --popover: #FFFDF8;
  --popover-foreground: #2C3E50;

  /* Primary = Colorado Navy Blue */
  --primary: #002868;
  --primary-foreground: #FFFFFF;

  /* Secondary = Colorado Gold */
  --secondary: #FFD700;
  --secondary-foreground: #2C3E50;

  /* Muted - warmer tones */
  --muted: #FFF8E7;
  --muted-foreground: #64748B;

  /* RETRO: Warm shadow colors */
  --shadow-warm: rgba(180, 140, 80, 0.2);
  --shadow-warm-lg: rgba(180, 140, 80, 0.25);

  /* Accent = Colorado Red */
  --accent: #BF0A30;
  --accent-foreground: #FFFFFF;

  --destructive: #DC2626;
  --border: #E8DCC8; /* Warmer border tone */
  --input: #E8DCC8;
  --ring: #002868;

  /* Chart colors using Colorado palette */
  --chart-1: #002868;
  --chart-2: #FFD700;
  --chart-3: #BF0A30;
  --chart-4: #2C3E50;
  --chart-5: #64748B;

  /* Sidebar */
  --sidebar: #FAFAFA;
  --sidebar-foreground: #2C3E50;
  --sidebar-primary: #002868;
  --sidebar-primary-foreground: #FFFFFF;
  --sidebar-accent: #E2E8F0;
  --sidebar-accent-foreground: #2C3E50;
  --sidebar-border: #E2E8F0;
  --sidebar-ring: #002868;
}

.dark {
  --background: #1A1A2E;
  --foreground: #FAFAFA;
  --card: #2C3E50;
  --card-foreground: #FAFAFA;
  --popover: #2C3E50;
  --popover-foreground: #FAFAFA;
  /* Lighter Colorado Blue for dark mode */
  --primary: #4A7AB3;
  --primary-foreground: #FFFFFF;
  --secondary: #FFD700;
  --secondary-foreground: #1A1A2E;
  --muted: #2C3E50;
  --muted-foreground: #94A3B8;
  /* Lighter Colorado Red for dark mode */
  --accent: #D44A5C;
  --accent-foreground: #FFFFFF;
  --destructive: #EF4444;
  --border: rgba(255, 255, 255, 0.1);
  --input: rgba(255, 255, 255, 0.15);
  --ring: #4A7AB3;
  --chart-1: #4A7AB3;
  --chart-2: #FFD700;
  --chart-3: #D44A5C;
  --chart-4: #94A3B8;
  --chart-5: #64748B;
  --sidebar: #2C3E50;
  --sidebar-foreground: #FAFAFA;
  --sidebar-primary: #4A7AB3;
  --sidebar-primary-foreground: #FFFFFF;
  --sidebar-accent: #1A1A2E;
  --sidebar-accent-foreground: #FAFAFA;
  --sidebar-border: rgba(255, 255, 255, 0.1);
  --sidebar-ring: #4A7AB3;
}

@layer base {
  *,
  ::before,
  ::after {
    --tw-border-opacity: 1;
    border-color: transparent; /* Don't apply border-border globally - it causes visible lines */
  }
  *:where(:not(html, body, svg, svg *)) {
    @apply outline-ring/50;
  }
  body {
    @apply bg-background text-foreground font-sans antialiased;
  }
  /* Only apply border color to elements that explicitly have a border width */
  [class*="border-"] {
    border-color: var(--border);
  }
}

@layer components {
  .section-padding {
    @apply py-12 md:py-16 lg:py-20;
  }

  .container-tight {
    @apply max-w-5xl mx-auto px-4 sm:px-6;
  }

  /* RETRO: Warm playful shadows */
  .shadow-retro {
    box-shadow: 0 4px 20px rgba(180, 140, 80, 0.15),
                0 8px 32px rgba(180, 140, 80, 0.1);
  }

  .shadow-retro-lg {
    box-shadow: 0 8px 30px rgba(180, 140, 80, 0.2),
                0 12px 48px rgba(180, 140, 80, 0.15);
  }

  .shadow-retro-hover {
    box-shadow: 0 12px 40px rgba(180, 140, 80, 0.25),
                0 16px 56px rgba(180, 140, 80, 0.18);
  }

  /* RETRO: Card with warm styling */
  .card-retro {
    @apply rounded-3xl border-2 border-border;
    background: var(--card);
    box-shadow: 0 4px 20px rgba(180, 140, 80, 0.12),
                0 2px 8px rgba(180, 140, 80, 0.08);
    transition: all 0.3s ease;
  }

  .card-retro:hover {
    box-shadow: 0 8px 32px rgba(180, 140, 80, 0.18),
                0 4px 16px rgba(180, 140, 80, 0.12);
    transform: translateY(-2px);
  }

  /* RETRO: Subtle grain texture overlay */
  .bg-grain {
    position: relative;
  }
  .bg-grain::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
    opacity: 0.03;
    pointer-events: none;
    z-index: 1;
  }
}

@layer utilities {
  .hand {
    font-family: var(--font-hand);
    font-weight: 500;
    letter-spacing: 0.005em;
  }

  /* 2026-08-06: `white-space: nowrap` was global here, so any eyebrow longer
     than the viewport forced the whole page into a horizontal scroll. The
     BrandShowcase note ("The versatile commuter. Step-through, low standover,
     surprising payload.") rendered 597px wide on a 390px phone and was the
     single largest contributor to the home page's 246px overflow.

     Short eyebrows are the common case and still don't wrap, because `balance`
     only takes effect once the text actually needs a second line. Long ones now
     wrap instead of breaking the page layout. */
  .eyebrow {
    font-family: var(--font-body);
    text-transform: uppercase;
    letter-spacing: 0.18em;
    font-size: 11px;
    font-weight: 600;
    color: var(--muted-foreground);
    text-wrap: balance;
    overflow-wrap: break-word;
  }
}
